import axios from "axios";
import { toast } from "sonner";

/**
 * API base URL resolution.
 *
 * Why this is non-trivial: production deploys can ship a bundle with
 * REACT_APP_BACKEND_URL pointing at Emergent's internal host
 * (e.g. https://seedbank-pro.emergent.host) while the user's browser is
 * on the custom domain (e.g. https://thecannaforge.com). That makes every
 * auth call cross-origin → the auth cookie is scoped to the API host, not
 * the page host → iOS Safari ITP blocks it as a 3rd-party cookie → user
 * is stuck in a "session expired" loop on mobile and can never reach the
 * dashboard.
 *
 * Resolution order:
 *   1. If running in a browser, ALWAYS prefer same-origin (`/api`).
 *      Same-origin requests are always first-party, so cookies + CORS
 *      cease to be a problem regardless of which custom domain the user
 *      loaded the page on.
 *   2. SSR / non-browser fallback: REACT_APP_BACKEND_URL + /api.
 */
const ENV_BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API =
  typeof window !== "undefined" && window.location && window.location.origin
    ? `${window.location.origin}/api`
    : `${ENV_BACKEND}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// v3.99bc — Field Access read-only enforcement.
// When the current tab is in eval mode (sessionStorage flag), any
// non-GET/HEAD request is short-circuited at the client layer with a
// friendly "Demonstration mode" toast. The synthetic evaluator user
// renders the same dashboard a real collector sees, but cannot mutate
// anything. Auth endpoints (`/auth/*`) are allowlisted so the synthetic
// user identity can still bootstrap via the AuthProvider — these are
// in-memory shims, never real auth calls (see AuthContext).
let _readOnlyToastShown = 0;
function _evalModeActiveSync() {
  try {
    return typeof window !== "undefined" &&
      window.sessionStorage.getItem("cf_eval_mode") === "1";
  } catch { return false; }
}
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (method === "get" || method === "head" || method === "options") return config;
  if (!_evalModeActiveSync()) return config;
  const url = config.url || "";
  // Auth endpoints must always work in eval mode — they're handled
  // entirely client-side by the AuthProvider for the synthetic user
  // (they never hit the network in practice, but allowlisting is the
  // safer rule).
  if (url.startsWith("/auth/")) return config;
  // Show a calm "Demonstration mode" toast ONCE per ~2.5s window so
  // multi-button forms / debounced submits don't spam. Downstream
  // `formatApiError(e)` returns the same friendly copy so the toast
  // reads consistently across call sites (sonner dedupes identical
  // messages shown back-to-back).
  const now = Date.now();
  if (now - _readOnlyToastShown > 2500) {
    _readOnlyToastShown = now;
    try {
      toast("Demonstration mode — submissions are disabled on the evaluator link.", {
        description: "All forms are visible for review. To submit, register a CannaForge account.",
        duration: 4000,
      });
    } catch { /* toast may not be mounted */ }
  }
  // Abort the request cleanly with a recognisable error code so callers
  // can detect this branch if they want a custom UI message.
  const err = new Error("Demonstration mode — submissions are disabled.");
  err.code = "EVAL_MODE_READ_ONLY";
  err.config = config;
  return Promise.reject(err);
});

/**
 * Session-aware 401 handler.
 *
 * Only treats 401s as "session expired" when we previously had a logged-in
 * user in this browser session. Anonymous 401s (a public page firing an
 * authed call before the user has logged in) are silently ignored — those
 * are the page's responsibility to handle inline.
 *
 * Why: without this guard, simply landing on /login fires the interceptor
 * for any background authed call, the toast appears, and the user is
 * re-routed to /login (where they already are) → glitch loop.
 */
let _sessionExpiredHandled = false;
let _hadUserSession = false;

export function _resetSessionExpiredFlag() { _sessionExpiredHandled = false; }
export function _markUserSessionStarted() { _hadUserSession = true; _sessionExpiredHandled = false; }
export function _markUserSessionEnded() { _hadUserSession = false; _sessionExpiredHandled = false; }

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // v3.99bc — Demo-mode rejected requests should not trigger the
    // session-expired flow. They're already user-facing via the toast
    // shown in the request interceptor.
    if (error?.code === "EVAL_MODE_READ_ONLY") {
      return Promise.reject(error);
    }
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthMe = url.endsWith("/auth/me") || url.includes("/auth/me?");
    const isAuthLogin = url.endsWith("/auth/login");
    if (status === 401 && !isAuthMe && !isAuthLogin && _hadUserSession) {
      if (!_sessionExpiredHandled) {
        _sessionExpiredHandled = true;
        try {
          toast.error("Your session has expired. Please sign in again.");
        } catch { /* toast may not be mounted yet */ }
        setTimeout(() => {
          if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
            const next = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `/login?next=${next}`;
          }
        }, 250);
      }
    }
    return Promise.reject(error);
  },
);

export function formatApiError(err) {
  // v3.99bc — Field Access evaluators see the canonical "Demonstration
  // mode" toast from the request interceptor. Downstream catch blocks
  // calling formatApiError get the same friendly copy so any rendered
  // toast (or inline error) reads cleanly. Sonner dedupes identical
  // toasts shown back-to-back, so a double-fire collapses.
  if (err?.code === "EVAL_MODE_READ_ONLY") {
    return "Demonstration mode — submissions are disabled.";
  }
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
